import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PickerArt } from './RoomPicker';
import AmbientLayer from './AmbientLayer';
import {
  peekCaption, peekEmptyLine, geraldLine, KEYHOLE_GERALD,
  GLASS_ZOOM, glassRadius, glassCenter, glassCatches, rimWarmth, rimColor, peekAside,
  telescopeHit, eyepieceLine, TELESCOPE_MOON_ASIDE,
  TELESCOPE, MOON_GERALD_ASIDE, MOON_GERALD_CAUGHT, MOON_GERALD_SNAP_MS, glassOverMoon,
} from '../../utils/roomPeek';
import {
  moonPalette, ROOF_NIGHT_CSS, useCalm, useMeteors, NightLayer, TelescopeEyepiece, WishButton, WishBar, PictureNews, SceneButtons, TeleGlow,
} from './RoofNight';
// 💬 09-26 option C: the event scene's chat as bubbles (+ the phone's full-screen scene)
import { SceneBubbles, SceneSay, SceneLog, ShowAllButton, useNarrow } from './SceneChat';
import { stageBoxFor } from '../../utils/roomPeek';
import PorchPumpkins from './PorchPumpkins'; // 🎃 10-07 pumpkins: the carved pumpkins on the porch picture
import { SceneCommands, CommandBar } from './SceneCommands'; // 🎛️ 10-09 the event's buttons on the scene

/**
 * 👁 Peek (ROOM_VERBS_PEEK_SPEC_2026_09_18 §4, W2 — frontend only).
 *   · RoomPeekLightbox — P1, the DOORWAY CAPTION: the room's art, and beneath
 *     it what everyone in there is up to (D1: lines UNDER the art).
 *   · RoomKeyhole — P3: look into ANOTHER room without leaving yours. A
 *     porthole (D2), who is inside, and one tap to go in.
 *
 *   · PeekGlass (09-19) — the magnifying glass over the doorway art, and the
 *     only place Gerald's eyes are ever drawn there.
 *
 * Pure presentation: every string arrives ready, every action is a callback,
 * the caller owns the portal, Esc and close-on-move.
 * ⭐⭐ No words on a picture. The porthole's vignette is glass, not text.
 * ⭐ This frontend has NO global box-sizing: every padded/bordered box that
 *   takes a width sets border-box itself.
 */
// ⭐⭐ ABOVE THE PHONE DOCK (09-19 audit). Both overlays are body portals, and
// on a phone the dock is `position: fixed; zIndex: 5000` with the bottom nav
// at 5100. The keyhole is OPENED FROM the picker, which lives inside that
// dock, so at the old 3000 it painted underneath the panel it came from:
// black screen, caption + `go in` + `close` all hidden. Same bug class this
// page has fixed three times (GameStepPage "rendered UNDERNEATH"). Stay over
// the nav, far under the tutorial's 100000.
const PEEK_Z = 5200;
const PARCH = '#e6dac6';
const SOFT = '#d9cdbb';
const GOLD = '#e9b565';
const LINE = 'rgba(122,96,60,0.55)';

// A middle dot between the label's pieces (never read aloud).
const DOT = { color: 'rgba(217,205,187,0.45)', padding: '0 7px' };
function dotted(nodes) {
  const out = [];
  nodes.forEach((n, i) => {
    if (i) out.push(<span key={`dot${i}`} aria-hidden="true" style={DOT}>·</span>);
    out.push(n);
  });
  return out;
}

function Caption({ roomId, here, align = 'center', inline = false, inRoom = false }) {
  const cap = peekCaption(here);
  // 👁 09-24 label (Nina: option A): the same lines, run together in a sentence
  // row under the picture. ONE owner for presence either way.
  if (inline) {
    const parts = cap.empty
      ? [<span key="empty">{peekEmptyLine(roomId, inRoom)}</span>]
      : cap.lines.map((l) => (
        <span key={l.name} style={{ overflowWrap: 'anywhere' }}>
          <b style={{ color: GOLD, fontWeight: 700 }}>{l.name}</b>{l.doing ? ` ${l.doing}` : ''}
        </span>
      ));
    if (cap.more > 0) parts.push(<span key="more" style={{ opacity: 0.7 }}>and {cap.more} more</span>);
    return <span data-testid="peek-caption">{dotted(parts)}</span>;
  }
  return (
    <div data-testid="peek-caption" style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, lineHeight: 1.45, color: SOFT, textAlign: align, maxWidth: '100%' }}>
      {cap.empty && <div>{peekEmptyLine(roomId, inRoom)}</div>}
      {cap.lines.map((l) => (
        <div key={l.name} style={{ overflowWrap: 'anywhere' }}>
          <b style={{ color: GOLD, fontWeight: 700 }}>{l.name}</b>{l.doing ? ` ${l.doing}` : ''}
        </div>
      ))}
      {cap.more > 0 && <div style={{ opacity: 0.7 }}>and {cap.more} more</div>}
    </div>
  );
}

/**
 * 🦝 Where's Gerald (spec §4 P5, D3): two eyes in a shadow until you tap them,
 * then the whole raccoon. Lives ONLY inside a peek. `spot` = % of its box.
 * The tap never bubbles (the lightbox closes on any tap, the keyhole panel
 * swallows its own), never steals focus from the message box, and the hit
 * box is 34x30 however small the eyes look (the 09-19 tap-target lesson).
 * ⭐⭐ No words on a picture: the eyes are empty spans, the found LINE is
 * rendered by the caller beneath / beside the art.
 */
const EYE = { display: 'block', width: 6, height: 6, borderRadius: '50%', background: '#ffd98a', boxShadow: '0 0 6px 2px rgba(255,200,110,0.75)' };
function GeraldSprite({ spot, found, onFind, size = 30, veiled = false, onReveal = null }) {
  if (!spot) return null;
  const at = { position: 'absolute', left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)' };
  if (found) {
    return (
      <span data-gerald="found" role="img" aria-label="Gerald the raccoon"
        style={{ ...at, fontSize: size, lineHeight: 1, pointerEvents: 'none', filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.75))' }}>🦝</span>
    );
  }
  return (
    <button type="button" data-gerald="eyes" aria-label="Something is watching from the shadows"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => { e.stopPropagation(); if (onFind) onFind(); }}
      // 🔍 veiled (the doorway peek): the dots are drawn ONLY inside the glass.
      // The button stays so a keyboard or a screen reader can still find him;
      // focusing it slides the glass over (a tell for keyboard users, on purpose).
      onFocus={onReveal || undefined}
      style={{ ...at, width: 34, height: 30, padding: 0, margin: 0, border: 0, background: 'transparent', cursor: veiled ? 'inherit' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, boxSizing: 'border-box' }}>
      {!veiled && <><span aria-hidden="true" style={EYE} /><span aria-hidden="true" style={EYE} /></>}
    </button>
  );
}
function GeraldFoundLine({ gerald, align = 'center' }) {
  if (!gerald || !gerald.found) return null;
  return <div data-testid="gerald-line" style={{ color: GOLD, fontSize: 13, fontWeight: 700, textAlign: align }}>{geraldLine(gerald.streak)}</div>;
}

/**
 * 🦝🧦 LOST & FOUND (LOST_AND_FOUND_SPEC_2026_09_24 W2; D7 ruled 09-24 late).
 * The thing Gerald took, drawn in the house's own ink-and-parchment hand: a
 * polka-dot BUNDLE or a paper TAG (the server rolls which, once per case). It
 * is drawn ONLY inside the glass, like his eyes: the painting itself never
 * shows it. No words on it (no words on a picture).
 */
const INK = '#2a1a10';
function LostDrawing({ look }) {
  if (look === 'tag') {
    return (
      <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
        <ellipse cx="22" cy="35.5" rx="11" ry="2.6" fill="rgba(0,0,0,0.5)" />
        <path d="M13 22 C8 19 5 13 7.5 6.5" fill="none" stroke={INK} strokeWidth="1.4" strokeLinecap="round" />
        <g transform="rotate(-12 21 23)">
          <path d="M13 15 H31 a2 2 0 0 1 2 2 V29 a2 2 0 0 1 -2 2 H13 L8.5 26.5 V19.5 Z" fill="#efe2c3" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <circle cx="13" cy="23" r="3.1" fill="none" stroke="#b9a57c" strokeWidth="1.3" />
          <circle cx="13" cy="23" r="1.6" fill="#2c2117" />
        </g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true" style={{ display: 'block', overflow: 'visible' }}>
      <ellipse cx="20" cy="35.5" rx="13" ry="3" fill="rgba(0,0,0,0.5)" />
      <path d="M20 16 C16 9.5 11.5 8 10.5 11.2 C12.5 13.6 16.5 15 20 16 Z" fill="#b0443a" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M20 16 C24 9.5 28.5 8 29.5 11.2 C27.5 13.6 23.5 15 20 16 Z" fill="#b0443a" stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8.5 33.5 C4.5 25 9 16 20 16 C31 16 35.5 25 31.5 33.5 Z" fill="#b0443a" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="20" cy="16" r="2.4" fill="#933a30" stroke={INK} strokeWidth="1.3" />
      {[[13, 24], [20, 21.5], [27, 24.5], [16.5, 29.5], [24, 29.5], [10.5, 30], [29.5, 30]].map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.55" fill="#f3e6c8" />
      ))}
    </svg>
  );
}
/** The thing's own hidden button: a keyboard or a screen reader can still
 *  find it (focusing it slides the glass over), a mouse never sees it. */
function LostSprite({ spot, onReturn, onReveal = null }) {
  if (!spot) return null;
  return (
    <button type="button" data-lost="spot" aria-label="Something is tucked away here"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => { e.stopPropagation(); if (onReturn) onReturn(); }}
      onFocus={onReveal || undefined}
      style={{ position: 'absolute', left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)', width: 34, height: 30, padding: 0, margin: 0, border: 0, background: 'transparent', cursor: 'inherit', boxSizing: 'border-box' }} />
  );
}
function LostLine({ lost, align = 'center' }) {
  if (!lost || !lost.line) return null;
  return <div data-testid="lost-line" aria-live="polite" style={{ color: GOLD, fontSize: 13, fontWeight: 700, textAlign: align }}>{lost.line}</div>;
}

/** 🌕 09-26 THE MOON (roomPeek.moonSkyFor): drawn, never painted (a painted
 *  full moon would lie on every other night). Placed and sized in % of the
 *  painting, so the SAME element reads right on the picture and 2x inside the
 *  glass. Gradients only, no px shadows (they would not scale with the glass).
 *  Harvest: deep orange low on the horizon, paling to gold as it climbs
 *  (moon.p 0 → 1). Blood: embers. The ink ring matches the paintings' outlines. */
const MOON_CRATERS = [
  'radial-gradient(circle at 62% 37%, rgba(140,62,26,0.22) 0 9%, rgba(140,62,26,0) 10%)',
  'radial-gradient(circle at 43% 63%, rgba(140,62,26,0.18) 0 12%, rgba(140,62,26,0) 13%)',
  'radial-gradient(circle at 70% 67%, rgba(140,62,26,0.15) 0 6%, rgba(140,62,26,0) 7%)',
  'radial-gradient(circle at 33% 41%, rgba(140,62,26,0.12) 0 7%, rgba(140,62,26,0) 8%)',
].join(', ');
/** 📸🌕 09-26: a /snap taken on a moon night carries `data.moon` (the server's
 *  houseSnap.snapMoonFor); this draws it on the photo in the chat, over the
 *  exact picture box (the peek frame's inline-grid idiom). No moon = the child as is.
 *  🌠 10-08: a meteor night's snap carries `data.meteors` = { streaks, star } (houseSnap.snapMeteorsFor):
 *  the same frame draws them frozen, in % of the painting. Neither = the child as is. */
export function MoonOver({ moon, meteors = null, children }) {
  const streaks = (meteors && Array.isArray(meteors.streaks) ? meteors.streaks : []).filter(snapStreakOk).slice(0, 6);
  const star = meteors && snapStreakOk(meteors.star) ? meteors.star : null;
  if ((!moon && !streaks.length && !star) || !React.isValidElement(children)) return children || null;
  return (
    <div data-snap-moon={moon ? (moon.tone || '') : undefined} data-snap-meteors={(streaks.length || star) ? String(streaks.length) : undefined}
      style={{ display: 'inline-grid', maxWidth: '100%', verticalAlign: 'top' }}>
      {React.cloneElement(children, { style: { ...(children.props.style || {}), gridArea: '1 / 1' } })}
      <div aria-hidden="true" style={{ gridArea: '1 / 1', position: 'relative', minWidth: 0, minHeight: 0, overflow: 'hidden', borderRadius: 10, pointerEvents: 'none' }}>
        {moon && <MoonDisc moon={moon} />}
        {streaks.map((m, i) => <SnapStreak key={`m${i}`} m={m} />)}
        {star && <SnapStreak m={star} big />}
      </div>
    </div>
  );
}

// 🌠 10-08: one frozen streak on a snap (RoofNight's Meteor / FallingStar look, no animation): the head is
// the bright end at (x, y), the tail fades along `angle`. Numbers only, all in % of the painting.
const snapNum = (v) => typeof v === 'number' && Number.isFinite(v);
function snapStreakOk(m) {
  return !!m && snapNum(m.x) && snapNum(m.y) && snapNum(m.len) && snapNum(m.angle)
    && m.x >= 0 && m.x <= 100 && m.y >= 0 && m.y <= 100 && m.len > 0 && m.len <= 40;
}
function SnapStreak({ m, big = false }) {
  return (
    <span data-snap-streak={big ? 'star' : 'meteor'} style={{
      position: 'absolute', left: `${m.x}%`, top: `${m.y}%`, width: `${m.len}%`, height: big ? 'max(2.5px, 0.9%)' : 'max(1.5px, 0.45%)',
      borderRadius: 999, transformOrigin: '0 50%', transform: `rotate(${m.angle}deg)`, pointerEvents: 'none',
      background: big
        ? 'linear-gradient(90deg, #ffffff 0%, #fff6d6 5%, rgba(255,232,170,0.75) 20%, rgba(255,220,150,0) 100%)'
        : 'linear-gradient(90deg, #fffdf2 0%, rgba(255,246,220,0.85) 10%, rgba(255,236,200,0) 100%)',
      ...(big ? { filter: 'drop-shadow(0 0 3px rgba(255,240,200,0.7))' } : {}),
    }} />
  );
}

// 🦝🌕 09-26 (Nina): Gerald standing on the moon's face, waving. Drawn ONLY in
// the glass's copy (and on a snap taken after catching him up there).
function MoonGerald() {
  return (
    <svg data-moon-gerald="" viewBox="0 0 60 50" aria-hidden="true" style={{
      position: 'absolute', left: '52%', top: '55%', width: '68%', transform: 'translate(-50%, -50%)', overflow: 'visible', pointerEvents: 'none',
    }}>
      <g stroke="#2a1c10" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
        <path d="M18 40 C 6 42, 2 32, 8 26 C 12 22, 16 28, 14 32" fill="#8a8078" />
        <path d="M5 30 l 4 -2 M4 35 l 5 -1 M8 39 l 4 -3" fill="none" />
        <ellipse cx="30" cy="38" rx="12.5" ry="10.5" fill="#8a8078" />
        <circle cx="30" cy="20" r="10.5" fill="#9a9088" />
        <path d="M21.5 13 L 20 5 L 26 10 Z M38.5 13 L 40 5 L 34 10 Z" fill="#9a9088" />
        <ellipse cx="30" cy="20" rx="9.5" ry="3.8" fill="#1c1510" />
        <path d="M41 31 L 49 20" fill="none" />
        <circle cx="50" cy="18.5" r="2.6" fill="#9a9088" />
        <ellipse cx="30" cy="25.5" rx="3.2" ry="2.2" fill="#e8e0d4" />
        <circle cx="30" cy="24.6" r="1.1" fill="#1c1510" stroke="none" />
      </g>
      <circle cx="26.2" cy="20" r="1.5" fill="#fff" />
      <circle cx="33.8" cy="20" r="1.5" fill="#fff" />
    </svg>
  );
}

export function MoonDisc({ moon, gerald = false }) {
  if (!moon || !(moon.size > 0)) return null;
  const { c, glow } = moonPalette(moon); // RoofNight: the eyepiece draws the same moon
  return (
    <span data-peek-moon={moon.tone} aria-hidden="true" style={{
      position: 'absolute', left: `${moon.x}%`, top: `${moon.y}%`, width: `${moon.size}%`, aspectRatio: '1 / 1',
      transform: 'translate(-50%, -50%)', pointerEvents: 'none', transition: 'top 14s linear, width 14s linear',
    }}>
      <span style={{
        position: 'absolute', left: '50%', top: '50%', width: '360%', height: '360%', transform: 'translate(-50%, -50%)',
        borderRadius: '50%', background: `radial-gradient(circle, rgba(${glow},0.42) 0%, rgba(${glow},0.16) 30%, rgba(${glow},0) 62%)`,
      }} />
      <span style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: `radial-gradient(circle, rgba(0,0,0,0) 0 93%, rgba(64,30,10,0.6) 94% 100%), ${MOON_CRATERS}, radial-gradient(circle at 38% 34%, ${c[0]} 0%, ${c[1]} 40%, ${c[2]} 78%, ${c[3]} 100%)`,
      }} />
      {(gerald || moon.gerald === true) && <MoonGerald />}
    </span>
  );
}

/**
 * 🔍 THE PEEK GLASS (09-19, Nina: "a magnifying glass as the cursor and that's
 * how you find Gerald"; option C, the rim warms as you close in).
 * A brass circle rides the pointer and shows the painting at GLASS_ZOOM. His
 * eyes are drawn ONLY inside it. It is on for EVERY peek, every room, every
 * day: a glass that showed up only in his room would give him away.
 *
 * Perf contract (PERF_TODO): pointer moves never re-render. One rAF-throttled
 * handler writes two transforms + one border color straight to the DOM. No
 * blur, no animated shadow, nothing runs while the pointer is still.
 * Phone: no cursor exists, so you DRAG the glass (it rides above the thumb)
 * and lifting with him inside catches him. Sweeping past is not a find.
 */
const GLASS_BRASS = rimColor(0);
function PeekGlass({ url, gerald, frameRef, api, roomId = null, day = null, onAside = null, thing = null, moon = null, nightLayer = null }) {
  const lensRef = useRef(null);
  const innerRef = useRef(null);
  const glintRef = useRef(null); // 🦝🧦 "something lost is near" (the warm rim stays Gerald's)
  const geo = useRef({ w: 0, h: 0, r: 75, c: null });
  const raf = useRef(0);
  const asideKey = useRef(null);
  const hunting = !!(gerald && gerald.spot && !gerald.found);
  const spot = gerald ? gerald.spot : null;
  const thingSpot = thing ? thing.spot : null;
  const moonUp = !!moon; // 🔭 on a moon night the telescope's aside says it can be looked through
  const moonRef = useRef(moon); // 🦝 the moon's live place for paint (it climbs; paint stays stable)
  moonRef.current = moon;

  const paint = useCallback(() => {
    const g = geo.current; const lens = lensRef.current; const inner = innerRef.current;
    if (!lens || !inner) return;
    if (!(g.w > 0) || !g.c) { lens.style.visibility = 'hidden'; return; }
    lens.style.visibility = 'visible';
    lens.style.width = `${g.r * 2}px`; lens.style.height = `${g.r * 2}px`;
    lens.style.transform = `translate(${g.c.x - g.r}px, ${g.c.y - g.r}px)`;
    inner.style.width = `${g.w * GLASS_ZOOM}px`; inner.style.height = `${g.h * GLASS_ZOOM}px`;
    inner.style.transform = `translate(${-(g.c.x * GLASS_ZOOM - g.r)}px, ${-(g.c.y * GLASS_ZOOM - g.r)}px)`;
    lens.style.borderColor = hunting ? rimColor(rimWarmth(g.c, spot, g.w, g.h)) : GLASS_BRASS;
    // same curve, its own cue: one opacity write, nothing animates
    if (glintRef.current) glintRef.current.style.opacity = thingSpot ? String(rimWarmth(g.c, thingSpot, g.w, g.h)) : '0';
    // 💬 the aside: the ONLY thing a move can re-render, and only when the
    // glass arrives at / leaves an object (a handful of times per peek)
    if (onAside) {
      // 🦝🌕 09-26: the glass on the moon finds Gerald up there (moon nights only)
      const a = (moonUp && glassOverMoon(g.c, moonRef.current, g.w, g.h))
        ? { key: 'moon.gerald', line: MOON_GERALD_ASIDE }
        : peekAside(roomId, g.c, g.w, g.h, day, asideKey.current);
      const key = a ? a.key : null;
      if (key !== asideKey.current) { asideKey.current = key; onAside(a ? ((moonUp && key === 'roof.telescope') ? TELESCOPE_MOON_ASIDE : a.line) : ''); }
    }
  }, [hunting, spot, thingSpot, roomId, day, onAside, moonUp]);

  const measure = useCallback(() => {
    const el = frameRef.current; if (!el) return;
    const rect = el.getBoundingClientRect(); const g = geo.current;
    const was = { w: g.w, h: g.h };
    g.w = rect.width; g.h = rect.height; g.r = glassRadius(rect.width);
    // at rest the glass sits left of center, over the room, never on his spot.
    // On a resize / rotate it keeps its PLACE IN THE PICTURE, not its pixels.
    if (!(was.w > 0) || !(was.h > 0) || !g.c) g.c = rect.width > 0 ? { x: rect.width * 0.36, y: rect.height * 0.5 } : null;
    else g.c = { x: (g.c.x / was.w) * g.w, y: (g.c.y / was.h) * g.h };
    paint();
  }, [frameRef, paint]);

  useEffect(() => {
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && frameRef.current) { ro = new ResizeObserver(measure); ro.observe(frameRef.current); }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); if (raf.current) cancelAnimationFrame(raf.current); };
  }, [measure, frameRef]);

  // the caller's handlers reach the glass through this (no state, no renders)
  api.current = {
    measure,
    moveTo(clientX, clientY, isTouch) {
      const el = frameRef.current; if (!el) return;
      const rect = el.getBoundingClientRect();
      const c = glassCenter(clientX, clientY, rect, isTouch);
      if (!c) return;
      // A move KNOWS the picture's size (it just read the rect), so it records
      // it: a pointer that moves before the first post-load measure must not
      // leave w = 0 behind, or that late measure reads "no previous size" and
      // snaps the glass back to its rest spot. (Defensive, by reading the
      // code. NOT a reproduced bug: the harness failure that prompted it turned
      // out to be headless Chrome not firing rAF in --dump-dom mode.)
      const g = geo.current;
      g.w = rect.width; g.h = rect.height; g.r = glassRadius(rect.width);
      g.c = c;
      if (!raf.current) raf.current = requestAnimationFrame(() => { raf.current = 0; paint(); });
    },
    moveToSpot(s) {
      const g = geo.current; if (!s || !(g.w > 0)) return;
      g.c = { x: (s.x / 100) * g.w, y: (s.y / 100) * g.h }; paint();
    },
    catches(s) { const g = geo.current; return !!s && glassCatches(g.c, s, g.w, g.h); },
  };

  return (
    <div ref={lensRef} data-peek-glass="" aria-hidden="true" style={{
      position: 'absolute', left: 0, top: 0, width: 150, height: 150, borderRadius: '50%', overflow: 'hidden',
      pointerEvents: 'none', visibility: 'hidden', boxSizing: 'border-box', background: '#2c2117',
      border: `4px solid ${GLASS_BRASS}`, boxShadow: '0 0 0 2px #1d150e, 0 8px 26px rgba(0,0,0,0.8)', willChange: 'transform',
    }}>
      <div ref={innerRef} style={{ position: 'absolute', left: 0, top: 0, willChange: 'transform' }}>
        <img src={url} alt="" draggable={false} style={{ display: 'block', width: '100%', height: '100%', maxWidth: 'none' }} />
        {/* 🌕 the moon is in the sky, so it is in the glass too (same % place, 2x).
            🦝 09-26: and only in here, Gerald is sitting on it. */}
        <MoonDisc moon={moon} gerald />
        {/* 🌠 the twist cloud, lanterns, meteors, the falling star: same % places, 2x */}
        {nightLayer}
        {/* 🦝🧦 the thing Gerald took, only ever in here. Drawn UNDER his eyes and
            a touch low, so on a day he is sitting on it they peek over the knot */}
        {thingSpot && (
          <span data-lost-glass={thing.look} style={{ position: 'absolute', left: `${thingSpot.x}%`, top: `${thingSpot.y}%`, transform: 'translate(-50%, -16%)', width: 'clamp(30px, 3.2%, 72px)', aspectRatio: '1 / 1', pointerEvents: 'none' }}>
            <LostDrawing look={thing.look} />
          </span>
        )}
        {hunting && (
          <span data-gerald-glass="" style={{ position: 'absolute', left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)', display: 'flex', gap: 5 }}>
            <span style={EYE} /><span style={EYE} />
          </span>
        )}
        {/* found: the glass magnifies HIM too, or it would show an empty shadow over a raccoon */}
        {gerald && spot && gerald.found && (
          <span style={{ position: 'absolute', left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)', fontSize: 34 * GLASS_ZOOM, lineHeight: 1, filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.75))' }}>🦝</span>
        )}
      </div>
      {/* the glint is glass, not text */}
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'radial-gradient(circle at 32% 26%, rgba(255,255,255,0.22), rgba(255,255,255,0) 42%)' }} />
      {/* ✦ D7: the "getting warmer" glint for a lost thing, on the rim */}
      {thingSpot && (
        <svg ref={glintRef} data-lost-glint="" viewBox="0 0 24 24" aria-hidden="true" style={{ position: 'absolute', top: '7%', right: '9%', width: '22%', maxWidth: 30, minWidth: 16, opacity: 0, pointerEvents: 'none' }}>
          <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill="rgba(255,236,170,0.45)" transform="translate(12 12) scale(1.35) translate(-12 -12)" />
          <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" fill="#fff8dc" />
          <path d="M19.5 1.5 L20.3 4.2 L23 5 L20.3 5.8 L19.5 8.5 L18.7 5.8 L16 5 L18.7 4.2 Z" fill="#fff8dc" />
        </svg>
      )}
    </div>
  );
}

/** P1. The room's art under the glass, and beneath it what everyone in there
 *  is up to. ⭐⭐ 09-19 CLOSE RULE: a click ON the picture is a LOOK now, never
 *  a close (with a search cursor people click around, and every miss used to
 *  dismiss the peek). Close = ← Back on the picture's top-left (09-24) ·
 *  anywhere outside the picture · Esc (the caller's). `gerald` = null on every day he is somewhere else: same
 *  DOM, same glass, nothing in it. */
/* `particles` = roomView.roomPeekScene(): { scene, field } or null. The page's
 *  ambient canvas sits at z 0 UNDER this sheet, so a room's particles could
 *  never show in a peek; here the same engine mounts INSIDE the picture and
 *  its field says where in the painting they come from (the tub, the hearth). */
/** 👁 09-24 THE LABEL (Nina: option A, "Give the room the width"). /look and
 *  the peek are ONE door (09-23), and the words now sit UNDER the picture on
 *  every screen, lined up with its edges like a museum label: the name and
 *  the mood on the first line; who is here, the TV, the weather and watchers
 *  on the second (the locked-room tease left 09-24, Nina: it read the same in
 *  every room). The old side column held ~380px of the
 *  window, and the art runs 2.3 times wider than tall, so the room shrank both
 *  ways (mockup https://claude.ai/artifact/14PDc6j62TooCwEsWRut74: 1037x454 →
 *  1400x613 at a 1440x800 window). Presence has ONE owner: the per-person
 *  Caption lives here whenever `look` is passed. The label swallows its own
 *  taps (people read and select text). No `look` = the old dialog, DOM
 *  untouched (roomPeek.test.js pins frame/caption as direct children). */
function LookLabel({ look, roomId, here }) {
  if (!look) return null;
  const tail = [look.tv ? 'The TV is on.' : null, look.weather, look.watchers].filter(Boolean);
  return (
    <div data-peek-look="label" onClick={(e) => e.stopPropagation()} style={{
      // width 0 + minWidth 100% = exactly the picture's width: the label wraps
      // inside the shrink-wrapped column and never widens it
      width: 0, minWidth: '100%', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left',
      fontSize: 13, lineHeight: 1.45, color: SOFT, cursor: 'default',
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 12, rowGap: 2 }}>
        <span style={{ color: PARCH, fontSize: 17, fontWeight: 700 }}>{look.name}</span>
        {look.flavor && <span style={{ fontStyle: 'italic' }}>{look.flavor}</span>}
      </div>
      <div>
        {dotted([
          <Caption key="who" roomId={roomId} here={here} inline inRoom />,
          ...tail.map((t) => <span key={t}>{t}</span>),
        ])}
      </div>
    </div>
  );
}

// First-paint guess for everything under the picture (padding, label, aside
// row). The real number is measured and replaces it at once.
const LOOK_RESERVE_GUESS = 150;
// 👁 09-24 (Nina: "too much empty space at the bottom"): with the label, the
// picture FILLS its box, width or height, whichever runs out first. It used to
// stop at its own pixels (the art is 1536x672) and at 82vh, and the column
// centered the leftover, so big windows got a dark band under the label. This
// caps the stretch so a huge monitor never gets mush.
const LOOK_MAX_UPSCALE = 1.5;
// 🌠 the star card's Go opens the wish box once per press, if the picture
// opens within this long of the press (the walk to the roof takes a moment)
const WISH_AUTO_OPEN_MS = 30 * 1000;
let consumedWishAt = 0;

export function RoomPeekLightbox({ url, roomId, line, here, onClose, gerald = null, particles = null, day = null, screen = null, below = null, hint = null, look = null, lost = null, sky = null, night = null, wish = null, news = null, glassButton = false, snap = null, share = null, chat = null, pumpkins = null, commands = null }) {
  // 💬 09-26 option C: `chat` = { lines, count, onSend|null, placeholder } on a
  // bubble event's own scene (roomPeek.sceneBubblesOn), else null: every other
  // picture renders exactly as before. On a narrow screen the scene goes FULL
  // SCREEN (the painting covers it, cropped around the moon), controls pinned.
  const chatOn = !!(chat && Array.isArray(chat.lines));
  const narrow = useNarrow();
  const stage = chatOn && narrow;
  const [logOpen, setLogOpen] = useState(false);
  useEffect(() => { if (!chatOn) setLogOpen(false); }, [chatOn]);
  const [view, setView] = useState(() => (typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : { w: 0, h: 0 }));
  useEffect(() => {
    if (!stage) return undefined;
    const on = () => setView((v) => (v.w === window.innerWidth && v.h === window.innerHeight ? v : { w: window.innerWidth, h: window.innerHeight }));
    on();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [stage]);
  // 🌕 09-26 `sky` = roomPeek.moonSkyFor(...) on a moon night in a room with a
  // moon spot, else null (every other picture byte-identical).
  const moon = sky && sky.moon ? sky.moon : null;
  // 🌠 09-26 roof night: `night` = roomPeek.nightSkyFor(...) (the twist cloud,
  // a meteor-shower sky, the falling star, wish lanterns) or null. `wish` =
  // { onWish(text) → Promise, openAt } on the roof while /wish is live, else
  // null (no button). The meteors live HERE so the picture and the glass draw
  // the same ones.
  const calm = useCalm();
  const meteors = useMeteors(night && night.meteors, calm);
  const [wishOpen, setWishOpen] = useState(false);
  const [eye, setEye] = useState(0); // 0 = closed, n = the nth look (picks the line)
  const eyeCount = useRef(0);
  const tapAt = useRef(null);
  // the star card's "🌠 Wish ▶" lands here with the box already open (once per press)
  const wishAt = wish && wish.openAt ? wish.openAt : 0;
  useEffect(() => {
    if (wishAt && wishAt !== consumedWishAt && Date.now() - wishAt < WISH_AUTO_OPEN_MS) { consumedWishAt = wishAt; setWishOpen(true); }
  }, [wishAt]);
  const hasWish = !!wish;
  useEffect(() => { if (!hasWish) setWishOpen(false); }, [hasWish]);
  // 🎛️ 10-09 (Nina): `commands` = { chips, onCommand } = the event's buttons on the picture (the
  // message box's own, utils/sceneCommands), else null: every other picture renders as before.
  // `askChip` = a chip that needs words, its one-line box open in the bottom slot.
  const commandChips = commands && Array.isArray(commands.chips) && commands.chips.length ? commands.chips : null;
  const [askChip, setAskChip] = useState(null);
  const commandKey = commandChips ? commandChips.join('|') : '';
  useEffect(() => { if (askChip && !(commandChips && commandChips.includes(askChip))) setAskChip(null); }, [commandKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Esc closes the eyepiece, then the wish box, before the picture: a capture
  // listener runs ahead of the page's own Esc (which closes the picture).
  useEffect(() => {
    if (!eye && !wishOpen && !logOpen && !askChip) return undefined; // 💬 09-26 option C: the message list closes before the picture too · 🎛️ 10-09 + a command's box
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation(); e.preventDefault();
      if (eye) setEye(0); else if (logOpen) setLogOpen(false); else if (askChip) setAskChip(null); else setWishOpen(false); // 💬 09-26 option C: the list first
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [eye, wishOpen, logOpen, askChip]); // 💬 09-26 option C: + logOpen · 🎛️ 10-09 + askChip
  // 🎬🍿 09-20: `screen` = { rect, label, onPlay } puts a ▶ ON the painted movie
  // screen (absent everywhere but a live screening's room). Pressing it pushes
  // in on the screen, then hands over to the real theater.
  const [pushing, setPushing] = React.useState(false);
  // (review L4) closing the picture inside the push-in must not seat you anyway
  const pushTimer = React.useRef(null);
  React.useEffect(() => () => { if (pushTimer.current) clearTimeout(pushTimer.current); }, []);
  const playScreen = (e) => {
    e.stopPropagation();
    if (!screen || !screen.onPlay || pushing) return;
    const calm = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (calm) { screen.onPlay(); return; }
    setPushing(true);
    pushTimer.current = setTimeout(() => { pushTimer.current = null; screen.onPlay(); }, 430);
  };
  const frameRef = useRef(null);
  const glass = useRef(null);
  const touchUpAt = useRef(0);
  const [aside, setAside] = useState('');
  // 🔍 09-26 (Nina): on an event scene the glass starts put away; the 🔍 under
  // Wish brings it out. Every glass call below checks glass.current first, so
  // a put-away glass is simply null (Gerald's hunt and the asides wait for it).
  const [glassOn, setGlassOn] = useState(!glassButton);
  useEffect(() => { setGlassOn(!glassButton); }, [glassButton]);
  const glassShown = !glassButton || glassOn;
  if (!glassShown) glass.current = null;
  // 🦝🌕 09-26 (Nina): the glass on the moon + a click = Gerald caught up there; for a
  // minute the 📸 glows and snaps him on the moon ("/snap gerald", the server draws him in)
  const [moonCatchAt, setMoonCatchAt] = useState(0);
  // 🔭 09-26 (Nina): a clickable thing glows very lightly until it has been used,
  // and lights up under the pointer (with the glass out or put away)
  const [teleHover, setTeleHover] = useState(false);
  const teleHoverRef = useRef(false);
  const [teleUsed, setTeleUsed] = useState(() => { try { return localStorage.getItem('peek.telescope.used') === '1'; } catch (e) { return false; } });
  // 👁 09-24 label: the picture gets every pixel of height the words under it
  // do not use. `reserve` = the dialog's padding + gaps + everything that is
  // not the picture, measured (a label wraps to 1-3 lines, the couch comes and
  // goes). The loop settles: a taller label narrows the picture, which can only
  // keep the label as tall or taller. jsdom has no ResizeObserver: the guess.
  const dialogRef = useRef(null);
  const [reserve, setReserve] = useState(LOOK_RESERVE_GUESS);
  const [natural, setNatural] = useState(null); // the art's own pixels, once loaded
  const hasLook = !!look;
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!hasLook || !dlg || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const frame = frameRef.current;
      if (!frame || pushTimer.current) return; // the push-in scales the frame
      const kids = Array.from(dlg.children);
      const cs = window.getComputedStyle(dlg);
      const gap = parseFloat(cs.rowGap) || 0;
      const used = kids.reduce((sum, el) => sum + el.getBoundingClientRect().height, 0);
      const next = Math.ceil((parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
        + used + gap * Math.max(0, kids.length - 1) - frame.getBoundingClientRect().height);
      setReserve((r) => (Math.abs(r - next) > 1 ? next : r));
    };
    const ro = new ResizeObserver(() => { if (!raf) raf = window.requestAnimationFrame(measure); });
    Array.from(dlg.children).forEach((el) => ro.observe(el));
    dlg.querySelectorAll('[data-peek-look], [data-peek-frame]').forEach((el) => ro.observe(el));
    return () => { ro.disconnect(); if (raf) window.cancelAnimationFrame(raf); };
  }, [hasLook, !!below, !!hint, !!(gerald && gerald.found), !!(lost && lost.line), url, !!(news && (news.card || news.poll)), chatOn, stage]); // eslint-disable-line react-hooks/exhaustive-deps
  // 💬 09-26 option C (the deps above gained chatOn + stage): the phone scene's painting box, cropped around the moon
  const stageBox = stage ? stageBoxFor(view.w, view.h, natural, moon ? { x: moon.x, y: moon.y } : null) : null;
  if (!url) return null;
  const spot = gerald && gerald.spot ? gerald.spot : null;
  // the height the label leaves the picture; with the art's ratio known, the
  // width that fills it (jsdom never loads an image: the max-limits alone)
  const fitH = `max(40vh, calc(100vh - ${reserve}px))`;
  const fill = (look && natural && natural.w > 0 && natural.h > 0) ? {
    width: `min(calc(100vw - 40px), calc(${fitH} * ${(natural.w / natural.h).toFixed(4)}), ${Math.round(natural.w * LOOK_MAX_UPSCALE)}px)`,
    height: 'auto',
  } : null;
  // on a plain /look nothing sits under the label, so the glass's whisper
  // floats in the bottom padding instead of holding a 30px row open
  const trailing = !!below || !!(hint || (screen && screen.rect && screen.onPlay));
  const floatAside = !!look && !trailing;
  // 🦝🧦 `lost` = { id, look, spot, onReturn, busy? } while it is here, or
  // { line } after a find. It hides at HIS spot, so on a day he is in this room
  // his eyes sit on it: one catch finds both (spec §3, "sitting on it").
  const lostSpot = lost && lost.id && lost.spot && lost.onReturn ? lost.spot : null;
  const returnLost = () => { if (lostSpot && !lost.busy) lost.onReturn(lost.id); };
  const sitting = !!(lostSpot && spot && !gerald.found);
  const findGerald = (gerald && gerald.onFind) ? () => { gerald.onFind(); if (sitting) returnLost(); } : null;
  const tryCatch = () => {
    if (!glass.current) return;
    if (spot && !gerald.found && findGerald && glass.current.catches(spot)) findGerald();
    else if (lostSpot && glass.current.catches(lostSpot)) returnLost();
  };
  // 🔭 on a moon night the roof's telescope is a real telescope: a click on it
  // opens the eyepiece instead of the aside (no moon = the asides, as ever)
  const lookThrough = (clientX, clientY) => {
    if (roomId !== 'roof' || !moon || !frameRef.current) return false;
    const r = frameRef.current.getBoundingClientRect();
    if (!(r.width > 0) || !(r.height > 0)) return false;
    if (!telescopeHit(((clientX - r.left) / r.width) * 100, ((clientY - r.top) / r.height) * 100, r.width, r.height)) return false;
    eyeCount.current += 1;
    setEye(eyeCount.current);
    if (!teleUsed) { setTeleUsed(true); try { localStorage.setItem('peek.telescope.used', '1'); } catch (e) { /* private mode */ } }
    return true;
  };
  const teleLive = roomId === 'roof' && !!moon;
  const catchMoonGerald = (clientX, clientY) => {
    if (!moon || !glassShown || !frameRef.current) return false;
    const r = frameRef.current.getBoundingClientRect();
    if (!glassOverMoon({ x: clientX - r.left, y: clientY - r.top }, moon, r.width, r.height)) return false;
    setMoonCatchAt(Date.now());
    setAside(MOON_GERALD_CAUGHT);
    return true;
  };
  const moonSnapHot = !!moonCatchAt && Date.now() - moonCatchAt < MOON_GERALD_SNAP_MS;
  const nightLayer = (night || meteors.length) ? <NightLayer night={night} moon={moon} meteors={meteors} calm={calm} /> : null;
  // 💬 09-26 option C: the picture's controls, built ONCE. They sit on the
  // picture's own corners on every screen but the phone's full-screen scene,
  // where the painting runs off the screen's edges and they pin to the screen.
  const controls = (
    <>
      {/* ← 09-24 (Nina: "just an arrow and say back"): ONE way back, where
          back lives on every app, the picture's top-left. It replaced the
          × (top-right) AND the "← Back to chat" pill on the bar under
          everything: two controls for one action. Solid, never tinted
          (translucent controls over art vanish). A control, not words on
          the picture. Esc and the dark outside still close. */}
      <button type="button" data-peek-back="1" aria-label="Back to chat" title="Back to chat"
        onClick={(e) => { e.stopPropagation(); if (onClose) onClose(); }}
        style={{
          position: 'absolute', top: 10, left: 10, height: 34, padding: '0 13px 0 10px',
          display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, cursor: 'pointer',
          font: 'inherit', fontSize: 13.5, fontWeight: 700, lineHeight: 1, textShadow: 'none',
          background: '#2c2117', border: `1px solid ${LINE}`, color: PARCH, boxSizing: 'border-box',
          boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
        }}>
        <svg aria-hidden="true" width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 8H3.5M7.5 4 3.5 8l4 4" /></svg>
        Back
      </button>
      {/* 🎛️ 10-09 (Nina): the event's buttons under Back (the message box's own, event first) */}
      {commandChips && (
        <SceneCommands chips={commandChips} onCommand={commands.onCommand} asking={askChip}
          onAsk={(c) => { setWishOpen(false); setAskChip(c); }} />
      )}
      {/* 🌠 09-26 the wish box: a button on the picture's top-right, the
          one-line box over its bottom edge (both solid: over art) */}
      {wish && <WishButton open={wishOpen} onToggle={() => { setAskChip(null); setWishOpen((o) => !o); }} />}
      {(glassButton || (snap && snap.onSnap) || (share && share.onShare)) && (
        <SceneButtons belowWish={!!wish}
          glass={glassButton ? { on: glassOn, onToggle: () => { setGlassOn((o) => !o); setAside(''); } } : null}
          snapHot={moonSnapHot}
          onSnap={snap && snap.onSnap ? () => { if (moonSnapHot) setMoonCatchAt(0); snap.onSnap(moonSnapHot ? 'gerald' : ''); } : null}
          onShare={share && share.onShare ? share.onShare : null} />
      )}
    </>
  );
  // 💬 09-26 option C: ONE slot on the picture's bottom edge (the phone scene's
  // bottom dock): the wish box while it is open, else the scene's message box
  const slot = (askChip && commandChips) // 🎛️ 10-09: a command that needs words asks for them here
    ? <CommandBar key={askChip} chip={askChip} onCommand={commands.onCommand} onClose={() => setAskChip(null)} docked={stage} />
    : (wish && wishOpen)
    ? <WishBar onWish={wish.onWish} onClose={() => setWishOpen(false)} docked={stage} />
    : ((chatOn && chat.onSend) ? <SceneSay onSend={chat.onSend} placeholder={chat.placeholder || undefined} docked={stage} /> : null);
  const artBits = {};
  artBits.frame = (
      <div ref={frameRef} data-peek-frame="" data-gerald-frame={spot ? '' : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (Date.now() - touchUpAt.current < 600) return; // the touch lift already looked
          if (glass.current) glass.current.moveTo(e.clientX, e.clientY, false);
          if (lookThrough(e.clientX, e.clientY)) return;
          if (catchMoonGerald(e.clientX, e.clientY)) return;
          tryCatch();
        }}
        onPointerLeave={() => { if (teleHoverRef.current) { teleHoverRef.current = false; setTeleHover(false); } }}
        onPointerMove={(e) => {
          if (teleLive && e.pointerType !== 'touch') {
            const r = e.currentTarget.getBoundingClientRect();
            const over = r.width > 0 && r.height > 0 && telescopeHit(((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100, r.width, r.height);
            if (over !== teleHoverRef.current) { teleHoverRef.current = over; setTeleHover(over); }
          }
          if (e.pointerType === 'touch' && !(e.currentTarget.hasPointerCapture && e.currentTarget.hasPointerCapture(e.pointerId))) return;
          if (glass.current) glass.current.moveTo(e.clientX, e.clientY, e.pointerType === 'touch');
        }}
        onPointerDown={(e) => {
          if (e.pointerType !== 'touch' || (e.target.closest && e.target.closest('button'))) return;
          tapAt.current = { x: e.clientX, y: e.clientY };
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* the drag still works per-move */ }
          if (glass.current) glass.current.moveTo(e.clientX, e.clientY, true);
        }}
        onPointerUp={(e) => {
          if (e.pointerType !== 'touch' || (e.target.closest && e.target.closest('button'))) return;
          touchUpAt.current = Date.now();
          if (glass.current) glass.current.moveTo(e.clientX, e.clientY, true);
          // a TAP on the telescope looks through it (a drag that ends there does not)
          const tap = tapAt.current; tapAt.current = null;
          if (tap && Math.hypot(e.clientX - tap.x, e.clientY - tap.y) < 12 && lookThrough(e.clientX, e.clientY)) return;
          if (tap && Math.hypot(e.clientX - tap.x, e.clientY - tap.y) < 12 && catchMoonGerald(e.clientX, e.clientY)) return;
          tryCatch();
        }}
        style={{
          display: 'inline-grid', position: 'relative', minHeight: 0, flex: '0 1 auto', cursor: glassShown ? 'none' : (teleHover ? 'pointer' : 'default'), touchAction: glassShown ? 'none' : 'manipulation', userSelect: 'none', WebkitUserSelect: 'none',
          ...(screen && screen.rect ? {
            transformOrigin: `${screen.rect.x + screen.rect.w / 2}% ${screen.rect.y + screen.rect.h / 2}%`,
            transition: 'transform 0.43s cubic-bezier(.6,.05,.3,1), opacity 0.43s ease',
            transform: pushing ? 'scale(4.2)' : 'none', opacity: pushing ? 0.2 : 1,
          } : {}),
        }}>
        <img src={url} alt="" draggable={false}
          onLoad={(e) => {
            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
            setNatural((n) => (n && n.w === w && n.h === h ? n : { w, h }));
            if (glass.current) glass.current.measure();
          }}
          style={(stage && stageBox)
            ? { gridArea: '1 / 1', display: 'block', width: stageBox.w, height: stageBox.h, maxWidth: 'none', maxHeight: 'none' } // 💬 09-26 option C: the phone scene covers the screen
            : { gridArea: '1 / 1', display: 'block', maxWidth: 'calc(100vw - 40px)', maxHeight: look ? fitH : 'min(82vh, max(40vh, calc(100vh - 282px)))', ...fill, borderRadius: 8, boxShadow: '0 10px 50px #000' }} />
        <div style={{ gridArea: '1 / 1', position: 'relative', minWidth: 0, minHeight: 0 }}>
          {/* first = underneath the raccoon, the glass and Back */}
          {particles && particles.scene && (
            <div data-peek-particles={particles.scene.atmosphere} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <AmbientLayer theme="airy" scene={particles.scene} field={particles.field} />
            </div>
          )}
          {/* 🌕 over the twinkling stars (it hides the ones behind it), under Gerald and the glass */}
          <MoonDisc moon={moon} />
          {/* 🌠 lanterns, the twist cloud, meteors, the falling star (RoofNight) */}
          {nightLayer}
          {/* 🎃 10-07 the porch's carved pumpkins (pumpkins = { porch, onOpen }, only on the porch while the table is out) */}
          {pumpkins && pumpkins.porch && <PorchPumpkins porch={pumpkins.porch} roomId={roomId} onOpen={pumpkins.onOpen} />}
          {/* 🔭 the telescope's light glow (until used) + its highlight under the pointer */}
          {teleLive && <TeleGlow x={TELESCOPE.x} y={TELESCOPE.y} hot={teleHover} idle={!teleUsed} />}
          {spot && (
            <GeraldSprite spot={spot} found={gerald.found} onFind={findGerald} size={34} veiled
              onReveal={() => { if (glass.current) glass.current.moveToSpot(spot); }} />
          )}
          {/* his button covers it on the days he is sitting on it */}
          {lostSpot && !sitting && (
            <LostSprite spot={lostSpot} onReturn={returnLost} onReveal={() => { if (glass.current) glass.current.moveToSpot(lostSpot); }} />
          )}
          {/* 💬 09-26 option C: the scene's newest lines, under the glass and the buttons */}
          {!stage && chatOn && !logOpen && <SceneBubbles lines={chat.lines} calm={calm} />}
          {glassShown && (
            <PeekGlass url={url} gerald={gerald} frameRef={frameRef} api={glass} roomId={roomId} day={day} onAside={setAside}
              thing={lostSpot ? { spot: lostSpot, look: lost.look } : null} moon={moon} nightLayer={nightLayer} />
          )}
          {screen && screen.rect && screen.onPlay && (
            <button type="button" data-peek-screen-play="1" aria-label={screen.label || 'Watch with the room'} title={screen.label || 'Watch with the room'}
              onClick={playScreen} onPointerDown={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', left: `${screen.rect.x}%`, top: `${screen.rect.y}%`, width: `${screen.rect.w}%`, height: `${screen.rect.h}%`,
                padding: 0, border: 0, borderRadius: 3, cursor: 'pointer', boxSizing: 'border-box', font: 'inherit',
                display: 'grid', placeItems: 'center',
                background: 'radial-gradient(ellipse at center, rgba(255,246,216,0.55), rgba(255,246,216,0) 75%)',
              }}>
              <span aria-hidden="true" style={{
                width: 'min(46%, 56px)', aspectRatio: '1 / 1', borderRadius: '50%', display: 'grid', placeItems: 'center',
                background: 'rgba(32,24,50,0.9)', color: '#ffd98a', boxShadow: '0 0 0 2px #ffd98a', fontSize: 'clamp(11px, 1.8vw, 22px)', lineHeight: 1,
              }}>▶</span>
            </button>
          )}
          {/* 💬 09-26 option C: Back, Wish and the scene row (built once above,
              pinned to the screen in the phone scene), then the whole message
              list over the picture when it is open, then the bottom slot */}
          {!stage && controls}
          {!stage && chatOn && logOpen && <SceneLog lines={chat.lines} onClose={() => setLogOpen(false)} />}
          {!stage && slot}
        </div>
      </div>
  );
  artBits.under = (
    <>
      <GeraldFoundLine gerald={gerald} />
      <LostLine lost={lost} />
      {/* 💬 the aside row ALWAYS holds its height: a line that appeared would
          re-center this column and slide the picture out from under the glass.
          On a plain /look it floats under the label instead (out of the
          layout, so it can never move the picture either). */}
      <div data-testid="peek-aside" aria-live="polite" style={{
        color: '#8fd8e8', fontSize: 13, fontStyle: 'italic', textAlign: 'center', maxWidth: 'min(560px, 100%)',
        ...(floatAside
          ? { position: 'absolute', top: '100%', left: 0, right: 0, margin: '2px auto 0', lineHeight: 1.25, pointerEvents: 'none' }
          : { minHeight: 20 }),
      }}>{aside}</div>
    </>
  );
  // 💬 09-26 option C: THE PHONE SCENE (mockup C, phone). The painting covers
  // the screen, cropped around the moon (or its middle); Back, Wish and the
  // scene row pin to the screen's corners; the news, the bubbles, "Show all"
  // and the message box stack up from the bottom over a dark fade. A tap on
  // the painting is still a look (the glass, the telescope, Gerald), never a close.
  if (stage) {
    const toColumn = { pointerEvents: 'auto', display: 'flex', justifyContent: 'center', minWidth: 0 };
    return (
      <div ref={dialogRef} role="dialog" aria-label="Room art" data-peek-stage="1" onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: PEEK_Z, boxSizing: 'border-box', overflow: 'hidden', background: '#0c0907', cursor: 'default',
      }}>
        {(nightLayer || wish || (roomId === 'roof' && moon)) && <style>{ROOF_NIGHT_CSS}</style>}
        {stageBox && (
          <div data-peek-stage-art="" style={{ position: 'absolute', left: stageBox.left, top: stageBox.top, width: stageBox.w, height: stageBox.h }}>
            {artBits.frame}
          </div>
        )}
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '55%', pointerEvents: 'none', background: 'linear-gradient(to bottom, rgba(12,9,7,0) 0%, rgba(12,9,7,0.85) 70%)' }} />
        <div data-peek-stage-chrome="" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ pointerEvents: 'auto' }}>{controls}</div>
        </div>
        {/* the mockup's spacing: the news from ~42% down, the bubbles + the box
            at the bottom; when a vote makes the news taller the bubbles give
            way (they shrink and clip from the top), nothing ever collides */}
        <div data-peek-stage-bottom="" style={{
          // the list opens in the space under the news (the news never jumps onto the moon);
          // centered and capped, so an upright tablet gets a column, not a stretched bar
          position: 'absolute', left: 0, right: 0, margin: '0 auto', width: 'min(560px, calc(100% - 32px))',
          top: '42%', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'stretch', gap: 10, pointerEvents: 'none',
        }}>
          <div data-peek-stage-news="" style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <GeraldFoundLine gerald={gerald} />
            <LostLine lost={lost} />
            {aside && <div data-testid="peek-aside" aria-live="polite" style={{ color: '#8fd8e8', fontSize: 13, fontStyle: 'italic', textAlign: 'center', textShadow: '0 1px 3px rgba(0,0,0,0.85)' }}>{aside}</div>}
            {news && (news.card || news.poll) && <div style={toColumn}><PictureNews news={news} onWish={wish ? () => setWishOpen(true) : null} /></div>}
          </div>
          <div data-peek-stage-talk="" style={{ flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {logOpen
              ? <SceneLog lines={chat.lines} onClose={() => setLogOpen(false)} docked />
              : <SceneBubbles lines={chat.lines} calm={calm} stage />}
            {!logOpen && chat.count > 0 && <div style={{ ...toColumn, flex: '0 0 auto' }}><ShowAllButton count={chat.count} onOpen={() => setLogOpen(true)} docked /></div>}
            {slot && <div data-peek-stage-dock="" style={{ pointerEvents: 'auto', flex: '0 0 auto' }}>{slot}</div>}
          </div>
        </div>
        {/* 🔭 the eyepiece over everything; a click or Esc closes it, not the picture */}
        {eye > 0 && moon && <TelescopeEyepiece moon={moon} line={eyepieceLine(day, eye - 1)} calm={calm} onClose={() => setEye(0)} />}
      </div>
    );
  }
  return (
    <div ref={dialogRef} role="dialog" aria-label="Room art" onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: PEEK_Z, cursor: 'zoom-out', boxSizing: 'border-box',
      background: 'rgba(6,4,3,0.92)', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 12, padding: 20,
    }}>
      {(nightLayer || wish || (roomId === 'roof' && moon)) && <style>{ROOF_NIGHT_CSS}</style>}
      {/* 🔭 the eyepiece sits over the picture; a click or Esc closes it, not the picture */}
      {eye > 0 && moon && <TelescopeEyepiece moon={moon} line={eyepieceLine(day, eye - 1)} calm={calm} onClose={() => setEye(0)} />}
      {/* The frame is EXACTLY the picture (the art runs 2.07-2.76 wide, so no
          fixed ratio works): an inline-grid shrink-wraps the img and the
          overlay shares its cell. The img's limits are vh/vw because a %
          max-height cannot resolve inside a shrink-wrapped parent. With `look`
          the height limit is MEASURED (reserve, above); without it, 282px
          (250 + the always-present aside row) leaves room for the caption
          (≤6 lines + the entry line); on a short landscape phone the art gives
          way so the words never clip (this box centers and does not scroll). */}
      {/* 👁 09-24 label: with `look`, the picture and its label share one
          shrink-wrapped column, so the label is exactly as wide as the art.
          Order: picture, label, the found line, the aside row (the glass's
          whisper holds its height last, so the label hugs the picture).
          Without `look` everything stays a DIRECT child of the dialog. */}
      {look ? (
        <div data-peek-merge="label" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, maxWidth: '100%', minWidth: 0, minHeight: 0, flex: '0 1 auto' }}>
          {artBits.frame}
          {!chatOn && <LookLabel look={look} roomId={roomId} here={here} />}{/* 💬 09-26 option C (Nina): a bubble scene has no label, like the mockup */}
          {artBits.under}
        </div>
      ) : (
        <>
          {artBits.frame}
          {artBits.under}
        </>
      )}
      {/* 📰 09-26 the event's newest card + the open vote: this sheet covers the chat */}
      {news && (news.card || news.poll) && <PictureNews news={news} onWish={wish ? () => setWishOpen(true) : null} />}
      {/* 💬 09-26 option C: the whole list, in the scene (it opens over the picture); a bubble scene drops the caption too */}
      {chatOn && <ShowAllButton count={chat.count} open={logOpen} onOpen={() => setLogOpen((o) => !o)} />}
      {!look && !chatOn && <Caption roomId={roomId} here={here} inRoom />}
      {!look && !chatOn && line && <div style={{ color: PARCH, fontSize: 13, textAlign: 'center' }}>{line}</div>}
      {/* 🛋️ 09-23 batch 1 (MOVIE_NIGHT_UX_HANDOFF §3.4): the couch under the art
          during a screening (the page passes it; null everywhere else) */}
      {below && (
        <div data-peek-below="1" onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 'min(720px, 100%)', boxSizing: 'border-box', cursor: 'default' }}>
          {below}
        </div>
      )}
      {/* 🚪🎬 the next thing to do on a screening night. Back moved onto the
          picture (09-24), so the bar that held it is gone; the hint stays. */}
      {(hint || (screen && screen.rect && screen.onPlay)) && (
        <div data-peek-hint="1" onClick={(e) => e.stopPropagation()} style={{ color: PARCH, fontSize: 13, opacity: 0.85, textAlign: 'center', cursor: 'default' }}>
          {hint || 'Press play on the projector.'}
        </div>
      )}
    </div>
  );
}

/** P3. `card` = the picker card model for that room ({ id, name, art }). */
export function RoomKeyhole({ card, here, onGo, onClose, gerald = null }) {
  if (!card) return null;
  return (
    <div data-room-keyhole="1" role="dialog" aria-label={`Peek into ${card.name}`} onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: PEEK_Z, boxSizing: 'border-box',
      background: 'rgba(6,4,3,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      {/* clicks INSIDE the panel never close it */}
      <div onClick={(e) => e.stopPropagation()} style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 22,
        maxWidth: 'min(560px, 100%)', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box',
      }}>
        <div data-testid="keyhole-glass" style={{
          position: 'relative', width: 'min(220px, 62vw)', aspectRatio: '1 / 1', flex: '0 0 auto',
          borderRadius: '50%', overflow: 'hidden', boxSizing: 'border-box', background: '#2c2117',
          border: '3px solid #4a3a24', boxShadow: '0 0 0 6px #1d150e, 0 12px 40px #000',
        }}>
          <PickerArt art={card.art} />
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 50% 40%, transparent 35%, rgba(0,0,0,0.75) 100%)' }} />
          {/* 🦝 over the vignette, in the dark of the glass (the porthole crops
              the painting, so the painting's own hotspot means nothing here) */}
          {gerald && <GeraldSprite spot={KEYHOLE_GERALD} found={gerald.found} onFind={gerald.onFind} size={28} />}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180, flex: '1 1 180px', maxWidth: '100%' }}>
          <div style={{ fontWeight: 800, fontSize: 17, color: GOLD }}>{card.name}</div>
          <Caption roomId={card.id} here={here} align="left" />
          <GeraldFoundLine gerald={gerald} align="left" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            <button type="button" onClick={() => { if (onGo) onGo(card.id); }}
              style={{ font: 'inherit', fontSize: 12.5, padding: '4px 12px', borderRadius: 999, cursor: 'pointer', background: 'rgba(47,35,24,0.9)', border: `1px solid ${GOLD}`, color: GOLD }}>
              go in →
            </button>
            <button type="button" onClick={onClose}
              style={{ font: 'inherit', fontSize: 12.5, padding: '4px 12px', borderRadius: 999, cursor: 'pointer', background: 'rgba(47,35,24,0.9)', border: `1px solid ${LINE}`, color: PARCH }}>
              close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
